import { FC, useState } from 'react'
import { Box, Typography, Button, Alert, Stack } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import CheckIcon from '@mui/icons-material/Check'

interface Props {
  codes: string[]
}

/**
 * Exibe os códigos de recuperação do 2FA com ações de copiar/baixar. Reutilizado
 * no enrollment (LoginPage) e na tela de Segurança. Os códigos só aparecem aqui
 * uma única vez — o backend guarda apenas os hashes.
 */
export const RecoveryCodesDisplay: FC<Props> = ({ codes }) => {
  const [copied, setCopied] = useState(false)
  const texto = codes.join('\n')

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard indisponível — usuário pode baixar */
    }
  }

  const baixar = () => {
    const blob = new Blob(
      [`Códigos de recuperação — Originei\n(uso único, guarde em local seguro)\n\n${texto}\n`],
      { type: 'text/plain;charset=utf-8' },
    )
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'originei-codigos-recuperacao.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.8125rem' }}>
        Guarde estes códigos em local seguro. Cada um funciona <strong>uma única vez</strong> e
        substitui o app autenticador caso você perca o acesso. Não serão exibidos novamente.
      </Alert>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
          mb: 2,
          p: 2,
          borderRadius: '10px',
          backgroundColor: '#FAFAFA',
          border: '1px solid #F6F7F9',
        }}
      >
        {codes.map((c) => (
          <Typography
            key={c}
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.95rem',
              letterSpacing: '0.06em',
              color: '#17191D',
              textAlign: 'center',
            }}
          >
            {c}
          </Typography>
        ))}
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Button
          variant="outlined"
          size="small"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={copiar}
        >
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={baixar}>
          Baixar .txt
        </Button>
      </Stack>
    </Box>
  )
}
